// src/pages/grades/StudentGrades.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  ChartBarIcon,
  AcademicCapIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import { FireLoader } from '../../components/Loader';
import apiClient from '../../api/client';
import toast from 'react-hot-toast';

// Helper: red color for any number below 50
const getColorClass = (value) => {
  if (value === null || value === undefined || value === '-') return '';
  const num = Number(value);
  if (!isNaN(num) && num < 50) return 'text-red-500 font-semibold';
  return '';
};

// Helper: format term label
const formatTerm = (term) => term.replace('term_', 'Term ');

// Helper: format rank (e.g., 1 → 1st, 2 → 2nd, 3 → 3rd, 4 → 4th)
const formatRank = (rank) => {
  if (rank === null || rank === undefined || rank === '-') return '-';
  if (rank === 1) return '1st';
  if (rank === 2) return '2nd';
  if (rank === 3) return '3rd';
  return `${rank}th`;
};

// Helper: grade letter for the PDF report (kept for print)
const getGradeLetter = (score) => {
  if (score === null || score === undefined) return '-';
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
};

export default function StudentGrades() {
  const { user } = useAuth();
  const userRole = String(user?.role || '').toLowerCase();
  const canDownloadResults = ['admin', 'school_admin', 'super_admin', 'teacher'].includes(userRole);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [academicYear, setAcademicYear] = useState('');
  const [studentId, setStudentId] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [classInfo, setClassInfo] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  // Step 1: Get the student ID from the authenticated user
  useEffect(() => {
    const fetchStudentId = async () => {
      if (!user) {
        setLoading(false);
        toast.error('User not authenticated.');
        return;
      }

      if (user.student_id) {
        setStudentId(user.student_id);
        return;
      }

      try {
        const response = await apiClient.get(`/students/by-user/${user.id}`);
        if (response.data.success && response.data.data) {
          setStudentId(response.data.data.id);
        } else {
          toast.error('Could not find your student profile.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching student ID:', err);
        toast.error('Could not retrieve student information.');
        setLoading(false);
      }
    };

    fetchStudentId();
  }, [user]);

  // Step 2: Fetch student details and grades
  useEffect(() => {
    if (!studentId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const studentRes = await apiClient.get(`/students/${studentId}`);
        if (studentRes.data.success) {
          const student = studentRes.data.data;
          setStudentName(`${student.first_name} ${student.last_name}`);
          setClassInfo(student.class?.name || 'Not Assigned');
        }

        const response = await apiClient.get(`/students/${studentId}/grades`);
        if (response.data.success) {
          const data = response.data.data || [];
          const year = response.data.academic_year || '';

          const transformed = data.map((item) => {
            const terms = {};
            const termKeys = ['term_1', 'term_2', 'term_3'];
            termKeys.forEach((key) => {
              if (item.terms && item.terms[key]) {
                terms[key] = {
                  test1: item.terms[key].test1 ?? null,
                  test2: item.terms[key].test2 ?? null,
                  mean: item.terms[key].mean ?? null,
                  rank: item.terms[key].rank ?? null,
                };
              }
            });

            const yearlyMean = item.yearly?.mean ?? 0;
            const yearlyRank = item.yearly?.rank ?? undefined;
            return {
              id: item.subject,
              subject: item.subject,
              terms,
              yearlyMean,
              yearlyRank,
            };
          });

          setGrades(transformed);
          setAcademicYear(year);
        } else {
          toast.error(response.data.message || 'Failed to load grades.');
          setGrades([]);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.response?.status === 403) {
          toast.error('You do not have permission to view these grades.');
        } else {
          toast.error('Could not load your grades. Please try again.');
        }
        setGrades([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studentId]);

  // Compute totals per term and yearly
  const computeTotals = () => {
    const termTotals = { term_1: 0, term_2: 0, term_3: 0 };
    let yearlyTotal = 0;
    let count = 0;
    grades.forEach((subject) => {
      const termKeys = ['term_1', 'term_2', 'term_3'];
      termKeys.forEach((term) => {
        const termData = subject.terms[term];
        if (termData && termData.mean !== null) {
          termTotals[term] += termData.mean;
        }
      });
      yearlyTotal += subject.yearlyMean || 0;
      count++;
    });
    if (count > 0) {
      yearlyTotal = Math.round(yearlyTotal);
      Object.keys(termTotals).forEach((term) => {
        termTotals[term] = Math.round(termTotals[term]);
      });
    }
    const numSubjects = grades.length || 1;
    const termAverages = {
      term_1: Math.round(termTotals.term_1 / numSubjects),
      term_2: Math.round(termTotals.term_2 / numSubjects),
      term_3: Math.round(termTotals.term_3 / numSubjects),
    };
    const yearlyAvg = Math.round(yearlyTotal / numSubjects);
    return { termTotals, yearlyTotal, termAverages, yearlyAvg };
  };

  const { termTotals, yearlyTotal, termAverages, yearlyAvg } = computeTotals();
  const hasGrades = grades.length > 0;

  // ==================== LANDSCAPE REPORT CARD PDF ====================
  const generatePrintHTML = () => {
    const fullName = studentName || 'Student';
    const classInfoText = classInfo || 'Not Assigned';
    const year = academicYear || '';

    let subjectRows = '';
    grades.forEach((subject) => {
      const t1 = subject.terms.term_1?.mean ?? '-';
      const t2 = subject.terms.term_2?.mean ?? '-';
      const t3 = subject.terms.term_3?.mean ?? '-';
      const yearly = subject.yearlyMean ?? '-';
      const grade = getGradeLetter(yearly);
      subjectRows += `
        <tr>
          <td style="padding: 4px 8px; font-size: 11px; border: 1px solid #ccc; font-weight: 600;">${subject.subject}</td>
          <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${t1}</td>
          <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${t2}</td>
          <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${t3}</td>
          <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc; font-weight: bold;">${yearly}</td>
          <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc; font-weight: bold;">${grade}</td>
        </tr>
      `;
    });

    const totalsRow = `
      <tr style="background: #e8f0fe;">
        <td style="padding: 4px 8px; font-weight: bold; border: 1px solid #ccc;">Combine</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termTotals.term_1 || 0}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termTotals.term_2 || 0}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termTotals.term_3 || 0}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc; font-weight: bold;">${yearlyTotal}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">-</td>
      </tr>
    `;
    const avgRow = `
      <tr style="background: #f0f4ff;">
        <td style="padding: 4px 8px; font-weight: bold; border: 1px solid #ccc;">Average</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termAverages.term_1 || 0}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termAverages.term_2 || 0}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${termAverages.term_3 || 0}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc; font-weight: bold;">${yearlyAvg}</td>
        <td style="padding: 4px 8px; text-align: center; border: 1px solid #ccc;">${getGradeLetter(yearlyAvg)}</td>
      </tr>
    `;

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Student Report Card - ${fullName}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Times New Roman', Times, serif;
              padding: 20px 30px;
              color: #1a1a1a;
              background: white;
              font-size: 12px;
            }
            @page {
              size: A4 landscape;
              margin: 15mm 18mm;
            }
            .print-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 3px solid #1a3a5c;
              padding-bottom: 12px;
              margin-bottom: 18px;
            }
            .print-header-left h1 {
              font-size: 22px;
              color: #1a3a5c;
              font-weight: bold;
              letter-spacing: 1px;
            }
            .print-header-left p {
              color: #555;
              font-size: 11px;
              margin-top: 2px;
            }
            .print-header-right {
              text-align: right;
              font-size: 10px;
              color: #666;
            }
            .print-title {
              text-align: center;
              font-size: 18px;
              font-weight: bold;
              color: #1a3a5c;
              margin-bottom: 15px;
              letter-spacing: 1.5px;
            }
            .student-info {
              display: flex;
              justify-content: space-between;
              background: #f8f9fa;
              padding: 8px 15px;
              border: 1px solid #ddd;
              border-radius: 4px;
              margin-bottom: 18px;
              font-size: 12px;
            }
            .student-info span { font-weight: bold; }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 12px;
            }
            th {
              background: #e9ecef;
              border: 1px solid #ccc;
              padding: 6px 8px;
              text-align: center;
              font-size: 11px;
              font-weight: 700;
              text-transform: uppercase;
            }
            td {
              border: 1px solid #ccc;
              padding: 4px 8px;
              font-size: 11px;
            }
            .section-title {
              font-size: 14px;
              font-weight: bold;
              color: #1a3a5c;
              margin-bottom: 6px;
            }
            .summary-box {
              display: flex;
              justify-content: space-between;
              background: #f8f9fa;
              border: 1px solid #ddd;
              border-radius: 4px;
              padding: 8px 15px;
              margin-top: 12px;
              font-size: 12px;
            }
            .summary-box span { font-weight: bold; }
            .print-footer {
              text-align: center;
              margin-top: 20px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
              color: #888;
              font-size: 9px;
            }
            .print-watermark {
              position: fixed;
              bottom: 20px;
              right: 20px;
              opacity: 0.08;
              font-size: 45px;
              color: #1a3a5c;
              font-weight: bold;
              transform: rotate(-20deg);
              pointer-events: none;
            }
            @media print {
              .no-print { display: none !important; }
              body { padding: 15px 20px; }
            }
          </style>
        </head>
        <body>
          <div class="print-watermark">PAWA SCHOOL</div>

          <div class="print-header">
            <div class="print-header-left">
              <h1>PAWASCHOOL</h1>
              <p>Student Report Card</p>
            </div>
            <div class="print-header-right">
              <p>Generated: ${new Date().toLocaleDateString()}</p>
              <p>Time: ${new Date().toLocaleTimeString()}</p>
            </div>
          </div>

          <div class="print-title">ACADEMIC PERFORMANCE REPORT</div>

          <div class="student-info">
            <div><span>Student:</span> ${fullName}</div>
            <div><span>Class:</span> ${classInfoText}</div>
            <div><span>Academic Year:</span> ${year}</div>
            <div><span>Subjects:</span> ${grades.length}</div>
          </div>

          <div class="section-title">Subject Scores</div>
          <table>
            <thead>
              <tr>
                <th style="width:20%;">Subject</th>
                <th style="width:14%;">Term 1</th>
                <th style="width:14%;">Term 2</th>
                <th style="width:14%;">Term 3</th>
                <th style="width:16%;">Yearly Avg</th>
                <th style="width:12%;">Grade</th>
              </tr>
            </thead>
            <tbody>
              ${subjectRows}
              ${totalsRow}
              ${avgRow}
            </tbody>
          </table>

          <div class="summary-box">
            <div><span>Overall Average:</span> ${yearlyAvg}%</div>
            <div><span>Total Combined:</span> ${yearlyTotal}</div>
            <div><span>Subjects Passed:</span> ${grades.filter(s => (s.yearlyMean || 0) >= 50).length} / ${grades.length}</div>
            <div><span>Grade:</span> ${getGradeLetter(yearlyAvg)}</div>
          </div>

          <div class="print-footer">
            <p>This is a computer-generated document. No signature required.</p>
            <p>PawaSchool Management System | All Rights Reserved</p>
          </div>
        </body>
      </html>
    `;
  };

  const handleDownloadPDF = async () => {
    if (!canDownloadResults) {
      toast.error('Only administrators and teachers can download results.');
      return;
    }
    if (!hasGrades) {
      toast.error('No grades available to download.');
      return;
    }
    setIsDownloading(true);
    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (printWindow) {
        printWindow.document.write(generatePrintHTML());
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return <FireLoader fullScreen size="lg" text="Loading..." />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      {/* Header with Student Name, Class, and Academic Year */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AcademicCapIcon className="w-7 h-7 text-gray-700" />
            My Grades
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-white border border-gray-200 rounded-full px-4 py-1.5">
            <span className="text-sm font-medium text-gray-700">
              {grades.length} Subjects
            </span>
          </div>
          {hasGrades && canDownloadResults && (
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              {isDownloading ? 'Preparing...' : 'Download Results'}
            </button>
          )}
        </div>
      </div>

      {/* Student info row (name, class, academic year) */}
      {studentName && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <span className="font-semibold text-gray-800">Name: {studentName}</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <span>Class: <span className="font-medium text-gray-800">({classInfo || 'Not Assigned'})</span></span>
            {academicYear && (
              <span>Academic Year: <span className="font-medium text-gray-800">{academicYear}</span></span>
            )}
          </div>
        </div>
      )}

      {!hasGrades ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <ChartBarIcon className="w-20 h-20 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No grades available</p>
          <p className="text-sm text-gray-400 mt-1">Check back after your exams</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Grade Table (no grade letter column) */}
          <div className="overflow-x-auto p-4">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 font-semibold text-gray-600 bg-gray-50/50 rounded-tl-lg border border-gray-200">
                    Subject
                  </th>
                  {['Term 1', 'Term 2', 'Term 3'].map((term) => (
                    <th key={term} className="px-4 py-3 font-semibold text-gray-600 bg-gray-50/50 text-center border border-gray-200" colSpan="3">
                      {term}
                    </th>
                  ))}
                  <th className="px-4 py-3 font-semibold text-gray-600 bg-gray-50/50 text-center border border-gray-200" colSpan="2">
                    Yearly
                  </th>
                </tr>
                <tr className="border-b border-gray-100">
                  <th className="px-4 py-2 text-xs text-gray-400 font-medium border border-gray-200"></th>
                  {['Test1', 'Test2', 'Mean', 'Test1', 'Test2', 'Mean', 'Test1', 'Test2', 'Mean'].map((label, i) => (
                    <th key={i} className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">
                      {label}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">Mean</th>
                  <th className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">Rank</th>
                </tr>
              </thead>
              <tbody>
                {grades.map((item, index) => {
                  const terms = item.terms || {};
                  const yearly = { mean: item.yearlyMean, rank: item.yearlyRank };
                  return (
                    <tr key={index} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-800 border border-gray-100">
                        {item.subject}
                      </td>
                      {['term_1', 'term_2', 'term_3'].map((termKey) => {
                        const term = terms[termKey] || {};
                        return (
                          <React.Fragment key={termKey}>
                            <td className={`px-2 py-3 text-center border border-gray-100 ${getColorClass(term.test1)}`}>
                              {term.test1 ?? '-'}
                            </td>
                            <td className={`px-2 py-3 text-center border border-gray-100 ${getColorClass(term.test2)}`}>
                              {term.test2 ?? '-'}
                            </td>
                            <td className={`px-2 py-3 text-center font-semibold border border-gray-100 ${getColorClass(term.mean)}`}>
                              {term.mean ?? '-'}
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td className={`px-2 py-3 text-center font-bold border border-gray-100 ${getColorClass(yearly.mean)}`}>
                        {yearly.mean ?? '-'}
                      </td>
                      <td className="px-2 py-3 text-center text-sm text-gray-500 border border-gray-100">
                        {formatRank(yearly.rank)}
                      </td>
                    </tr>
                  );
                })}

                {/* ===== COMBINE OF ALL SUBJECTS ROW ===== */}
                <tr className="bg-gray-50/70 border-t border-gray-200">
                  <td className="px-4 py-3 font-semibold text-gray-700 border border-gray-100">
                    Combine of all Subjects
                  </td>
                  {['term_1', 'term_2', 'term_3'].map((termKey) => (
                    <React.Fragment key={termKey}>
                      <td className="px-2 py-3 text-center text-gray-500 border border-gray-100" colSpan="1">
                        -
                      </td>
                      <td className="px-2 py-3 text-center text-gray-500 border border-gray-100" colSpan="1">
                        -
                      </td>
                      <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">
                        {termTotals[termKey] || 0}
                      </td>
                    </React.Fragment>
                  ))}
                  <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">
                    {yearlyTotal}
                  </td>
                  <td className="px-2 py-3 text-center text-gray-500 border border-gray-100">-</td>
                </tr>

                {/* ===== AVERAGE ROW ===== */}
                <tr className="bg-gray-50/50 border-t border-gray-200">
                  <td className="px-4 py-3 font-semibold text-gray-700 border border-gray-100">
                    Average
                  </td>
                  {['term_1', 'term_2', 'term_3'].map((termKey) => (
                    <React.Fragment key={termKey}>
                      <td className="px-2 py-3 text-center text-gray-500 border border-gray-100" colSpan="1">
                        -
                      </td>
                      <td className="px-2 py-3 text-center text-gray-500 border border-gray-100" colSpan="1">
                        -
                      </td>
                      <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">
                        {termAverages[termKey] || 0}
                      </td>
                    </React.Fragment>
                  ))}
                  <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">
                    {yearlyAvg}
                  </td>
                  <td className="px-2 py-3 text-center text-gray-500 border border-gray-100">-</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer note */}
          <div className="p-4 border-t border-gray-200 bg-gray-50/50 text-xs text-gray-400 flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium text-gray-500">Mean = average of Test1 and Test2</span>
            <span>Rank based on class performance</span>
          </div>
        </div>
      )}
    </div>
  );
}